import {
    Button,
    Checkbox,
    Form,
    FormInstance,
    Radio,
    Space,
    Typography,
    Upload,
  } from "antd";
  import { Plus, PlusCircle, Trash2 } from "lucide-react";
  import { useEffect, useState } from "react";
  import { LoadingOutlined } from "@ant-design/icons";
  import { useTranslation } from "react-i18next";
import { extractImageExtension, getBase64 } from "../helpers";
import { Question } from "@types";

interface Props {
  form: FormInstance;
  question?: Question
}

export const MultipleImageChoiceQuestion = ({form, question}: Props) => {
    const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string[]>([]);
  // const [defaultCorrectAnswerIndex, setDefaultCorrectAnswerIndex] = useState<any>();

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
      type="button"
    >
      {loading ? <LoadingOutlined /> : <Plus />}
      <Typography.Text style={{ marginTop: 8 }}>
        {t("forms.questions.selectImage")}
      </Typography.Text>
    </button>
  );

  useEffect(() => {
    if (question && question.answers) {
      // Find the index of the correct answer to set as the default selected radio button
      // const correctAnswerIndex = question.base64Images.findIndex(answer => answer.isCorrect);
      setImageUrl(question.answers.map((image) => `data:image/${image.answerImageViewModel?.extension};base64,${image.answerImageViewModel?.base64Value}`));
      // setDefaultCorrectAnswerIndex(correctAnswerIndex >= 0 ? correctAnswerIndex : undefined);
    }
  }, [question]);

  const handleChange = async (info: any, index: number, name: any) => {
    setLoading(true); // Set loading to true when image starts uploading
    let url = await getBase64(info.file.originFileObj);
    setLoading(false);
    const newImageUrl = [...imageUrl]; // Copy existing imageUrls
    newImageUrl[index] = url; // Replace imageUrl for the specific index
    form.setFieldValue(["answers", name, "answerImageViewModel", "extension"], extractImageExtension(url));
    form.setFieldValue(["answers", name, "answerImageViewModel", "base64Value"], url.split(",")[1]); 
    setImageUrl(newImageUrl); 
  };

  return (
    <>
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.answers !== cur.answers}>
        {({ getFieldValue }) => {
          const answers = getFieldValue(["answers"]) || [];
          const hasAtLeastOneCorrect = answers.some((a: any) => a && a.isCorrect);
          if (hasAtLeastOneCorrect) return null;
          return (
            <div style={{ marginBottom: 8 }}>
              <Typography.Text strong type="warning">
                {t("forms.questions.oneAnswerCorrect")}
              </Typography.Text>
            </div>
          );
        }}
      </Form.Item>
      <Form.List name={["answers"]} initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <Radio.Group style={{ width: "100%" }}>
            <Space
              wrap
              style={{ display: "flex", marginBottom: 8 }}
              align="baseline"
            >
              {fields.map(({ key, name, ...restField }, index) => {
                return (
                  <Space key={key}>
                    <Form.Item
                      noStyle
                      initialValue={false}
                      {...restField}
                      name={[name, "isCorrect"]}
                      valuePropName="checked"
                    >
                     <Checkbox value={key} />
                    </Form.Item>

                    <Form.Item
                      style={{ width: "100%" }}
                      {...restField}
                      name={[name, "answerImageViewModel"]}
                      rules={[
                        {
                          required: true,
                          message: t("forms.questions.selectImage"),
                        },
                      ]}
                    >
                      <Upload
                        name="avatar"
                        listType="picture-card"
                        className="avatar-uploader"
                        showUploadList={false}
                        
                        onChange={(info) => handleChange(info, index, name)}
                      >
                        {imageUrl[index] ? (
                          <img
                            src={imageUrl[index]}
                            alt={t("forms.questions.selectImage")}
                            style={{ width: "100%" }}
                          />
                        ) : (
                          uploadButton
                        )}
                      </Upload>
                    </Form.Item>
                    <Button
                      icon={<Trash2 />}
                      danger
                      onClick={() => {
                        remove(name);
                        setImageUrl(imageUrl.filter((_, i) => i !== index));
                      }}
                    />
                  </Space>
                );
              })}
            </Space>
            <Form.Item noStyle>
              <Button
                type="dashed"
                icon={<PlusCircle />}
                block
                onClick={() => add()}
              >
                {t("forms.questions.addAnswer")}
              </Button>
            </Form.Item>
          </Radio.Group>
        )}
      </Form.List>
      {/* Hidden validator: ensure at least one correct answer is selected */}
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.answers !== cur.answers}>
        {() => (
          <Form.Item
            name={["answers"]}
            style={{ display: "none" }}
            rules={[
              {
                validator: (_, value) => {
                  const list = Array.isArray(value) ? value : [];
                  return list.some((answer) => answer && answer.isCorrect)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("forms.questions.selectAtLeastOneCorrect")));
                },
              },
            ]}
          >
            <span />
          </Form.Item>
        )}
      </Form.Item>
    </>
  );
}